// src/app/pages/campaigns/Sequence.jsx
import { useEffect, useState } from "react";
import axios from "../../lib/axios";
import { useParams } from "react-router-dom";
import Tabs from "./Tabs";
import { Plus, Trash2, Save, Mail, Clock, ChevronRight, CheckCircle2, AlertCircle, FlaskConical, FileText, Zap, Eye, X, Send, User, ChevronDown, RefreshCw } from "lucide-react";
import WysiwygEditor from "../../components/WysiwygEditor";
import "../Pages.css";

export default function Sequence() {
  const [steps, setSteps] = useState([
    { delay: 1, variants: [{ id: "A", templateId: "", subject: "", body: "", split: 50 }] },
  ]);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [activeVariantId, setActiveVariantId] = useState("A");
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const { id } = useParams();

  // Preview modal state
  const [showPreview, setShowPreview] = useState(false);
  const [previewContacts, setPreviewContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [selectedFromAccount, setSelectedFromAccount] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [renderedSubject, setRenderedSubject] = useState("");
  const [renderedBody, setRenderedBody] = useState("");
  const [renderError, setRenderError] = useState(null);

  // Sync active variant when step changes
  useEffect(() => {
    const step = steps[activeStepIndex];
    if (step && !step.variants.find((v) => v.id === activeVariantId)) {
      setActiveVariantId(step.variants[0]?.id || "A");
    }
  }, [activeStepIndex]);

  // Fetch master templates once
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await axios.get("master-templates/");
        const raw = res.data;
        const list = Array.isArray(raw)
          ? raw
          : Array.isArray(raw.data)
          ? raw.data
          : Object.values(raw);
        const normalized = list.map((t) => ({
          ...t,
          id: t.id ?? t._id ?? String(t),
          name: t.name ?? t.title ?? String(t),
          subject: t.subject ?? t.name ?? "Untitled",
          content: t.html || t.content || "<p>No content</p>",
        }));
        setTemplates(normalized);
      } catch (err) {
        console.error("Error fetching master templates:", err);
      }
    };
    fetchTemplates();
  }, []);

  // Load existing sequence from backend
  useEffect(() => {
    if (!id) return;
    const fetchSequence = async () => {
      try {
        const res = await axios.get(`email-campaign/get-sequence/${id}`);
        const data = res.data;
        if (data?.steps && Array.isArray(data.steps) && data.steps.length) {
          const normalizedSteps = data.steps.map((step) => ({
            delay: step.delayDays ?? step.delay ?? 1,
            variants: (step.variants ?? [])
              .slice(0, 2)
              .map((v, idx) => {
                let subject = v.subject ?? "";
                let body = v.body ?? "";
                if ((!subject || !body) && v.templateId && templates.length) {
                  const tpl = templates.find((t) => String(t.id) === String(v.templateId));
                  if (tpl) {
                    if (!subject) subject = tpl.subject || "";
                    if (!body) body = tpl.content || "";
                  }
                }
                return {
                  id: idx === 0 ? "A" : "B",
                  templateId: v.templateId ?? "",
                  subject,
                  body,
                  split: v.splitPercent ?? v.split ?? 50,
                };
              }),
          }));
          setSteps(normalizedSteps);
          setActiveStepIndex(0);
          setActiveVariantId("A");
        }
      } catch (err) {
        console.warn("No existing sequence or error fetching:", err);
      }
    };
    fetchSequence();
  }, [id]);

  const addStep = () => {
    const last = steps[steps.length - 1];
    const isConfigured = last.variants.some((v) => v.templateId || v.body);
    if (!isConfigured) {
      alert("Please configure at least one email before adding a new step.");
      return;
    }
    const newStep = { delay: 1, variants: [{ id: "A", templateId: "", subject: "", body: "", split: 50 }] };
    setSteps([...steps, newStep]);
    setActiveStepIndex(steps.length);
    setActiveVariantId("A");
  };

  const deleteStep = (index) => {
    if (steps.length === 1) return;
    const updated = steps.filter((_, idx) => idx !== index);
    setSteps(updated);
    const newActive = activeStepIndex === index
      ? Math.min(index, updated.length - 1)
      : activeStepIndex > index
      ? activeStepIndex - 1
      : activeStepIndex;
    setActiveStepIndex(newActive);
  };

  const updateVariant = (stepIndex, variantId, field, value) => {
    setSteps((prev) =>
      prev.map((step, sIdx) =>
        sIdx === stepIndex
          ? { ...step, variants: step.variants.map((v) => v.id === variantId ? { ...v, [field]: value } : v) }
          : step
      )
    );
  };

  const addVariant = () => {
    setSteps((prev) =>
      prev.map((step, sIdx) =>
        sIdx === activeStepIndex && step.variants.length === 1
          ? { ...step, variants: [...step.variants, { id: "B", templateId: "", subject: "", body: "", split: 50 }] }
          : step
      )
    );
    setActiveVariantId("B");
  };

  const deleteVariant = (variantId) => {
    setSteps((prev) =>
      prev.map((step, sIdx) =>
        sIdx === activeStepIndex
          ? { ...step, variants: step.variants.filter((v) => v.id !== variantId) }
          : step
      )
    );
    setActiveVariantId("A");
  };

  const saveSequence = async () => {
    setSaving(true);
    try {
      const payload = {
        campaignId: id,
        steps: steps.map((step) => ({
          delay: step.delay,
          variants: step.variants.map((v) => ({
            id: v.id,
            templateId: v.templateId ? String(v.templateId) : "",
            subject: v.subject || "",
            body: v.body || "",
            split: v.split,
          })),
        })),
      };
      await axios.post(`email-campaign/sequence/${id}`, payload);
      alert("Sequence saved successfully!");
      try {
        const tplRes = await axios.get("master-templates/");
        const raw = tplRes.data;
        const list = Array.isArray(raw) ? raw : Array.isArray(raw.data) ? raw.data : Object.values(raw);
        setTemplates(list.map((t) => ({
          ...t,
          id: t.id ?? t._id ?? String(t),
          name: t.name ?? t.title ?? String(t),
          subject: t.subject ?? t.name ?? "Untitled",
          content: t.html || t.content || "<p>No content</p>",
        })));
      } catch (_) {}
    } catch (err) {
      console.error("Error saving sequence:", err);
      alert("Failed to save sequence. Check console.");
    } finally {
      setSaving(false);
    }
  };

  // Fetch contacts and email accounts for preview modal
  const openPreview = async () => {
    setShowPreview(true);
    setPreviewLoading(true);
    try {
      const [contactsRes, accountsRes] = await Promise.all([
        axios.get(`contacts/get-leads/${id}`),
        axios.get("email/accounts"),
      ]);
      const contacts = Array.isArray(contactsRes.data)
        ? contactsRes.data
        : Array.isArray(contactsRes.data?.leads)
        ? contactsRes.data.leads
        : Array.isArray(contactsRes.data?.data)
        ? contactsRes.data.data
        : [];
      const accounts = accountsRes.data?.accounts || accountsRes.data || [];
      setPreviewContacts(contacts);
      setEmailAccounts(accounts);
      if (contacts.length > 0) setSelectedContact(contacts[0]);
      if (accounts.length > 0) setSelectedFromAccount(accounts[0].id);
    } catch (err) {
      console.error("Error fetching preview data:", err);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Replace {{variable}} placeholders with contact data
  const personalizeHtml = (html, contact) => {
    if (!html || !contact) return html || "";
    const fields = {
      name: contact.name || "",
      first_name: contact.name?.split(" ")[0] || contact.name || "",
      last_name: contact.name?.split(" ").slice(1).join(" ") || "",
      email: contact.email || "",
      phone: contact.phone || "",
      location: contact.location || "",
      linkedin: contact.linkedin || "",
      facebook: contact.facebook || "",
      ...(contact.custom_fields || {}),
    };
    return html.replace(/\{\{(\w+)\}\}/g, (_, key) => fields[key] ?? `{{${key}}}`);
  };

  const previewLiquid = async (bodyHtml) => {
    const res = await axios.post(`email-campaign/${id}/preview-render`, {
      subject: "",
      body: bodyHtml || "",
      contact_id: selectedContact?.id || null,
      account_id: selectedFromAccount || null,
      sequence_email_opened: false,
    });
    return res.data?.body || "";
  };

  const handleSendTestEmail = async () => {
    if (!selectedFromAccount) {
      alert("Please select an email account to send from.");
      return;
    }
    setSendingTest(true);
    try {
      await axios.post(`email-campaign/send-test/${id}`, {
        step_index: activeStepIndex,
        variant_id: activeVariant?.id || "A",
        from_account_id: selectedFromAccount,
        contact_id: selectedContact?.id || null,
      });
      alert("Test email sent successfully!");
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to send test email.");
    } finally {
      setSendingTest(false);
    }
  };

  const isStepConfigured = (step) => step.variants.some((v) => v.subject || v.body);

  const activeStep = steps[activeStepIndex];
  const activeVariant = activeStep?.variants.find((v) => v.id === activeVariantId) || activeStep?.variants[0];
  const hasAbTest = activeStep?.variants.length === 2;
  const splitA = hasAbTest ? activeStep.variants[0].split : 100;
  const splitB = hasAbTest ? activeStep.variants[1].split : 0;

  // Render the email-preview modal subject + body server-side via Liquid
  // whenever the user changes the sample contact, sender, or active variant.
  // Placed after activeVariant is declared to avoid a TDZ ReferenceError.
  useEffect(() => {
    if (!showPreview) return;
    const subjectIn = activeVariant?.subject || "";
    const bodyIn = activeVariant?.body || "";
    let cancelled = false;
    setRenderError(null);
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`email-campaign/${id}/preview-render`, {
          subject: subjectIn,
          body: bodyIn,
          contact_id: selectedContact?.id || null,
          account_id: selectedFromAccount || null,
          sequence_email_opened: false,
        });
        if (cancelled) return;
        setRenderedSubject(res.data?.subject ?? subjectIn);
        setRenderedBody(res.data?.body ?? bodyIn);
      } catch (err) {
        if (cancelled) return;
        setRenderError(err.response?.data?.detail || err.message || "Render failed");
        setRenderedSubject(subjectIn);
        setRenderedBody(bodyIn);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [showPreview, selectedContact, selectedFromAccount, activeVariantId, activeStepIndex, activeVariant?.subject, activeVariant?.body, id]);

  return (
    <div className="page animate-fade-in">
      <Tabs activeTab="Sequence" campaignId={id} />

      {/* ── Header ── */}
      <div className="page-header">
        <div className="header-info">
          <h2>Email Sequence</h2>
          <p>{steps.length} step{steps.length !== 1 ? "s" : ""} configured · Build your outreach flow</p>
        </div>
        <div className="header-actions">
          <button onClick={saveSequence} disabled={saving} className="btn btn-primary">
            {saving ? <span className="btn-loading" /> : <Save size={16} />}
            {saving ? "Saving…" : "Save Sequence"}
          </button>
        </div>
      </div>

      {/* ── Main Layout ── */}
      <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: "1.25rem", alignItems: "start" }}>

        {/* ════ LEFT — Visual Timeline ════ */}
        <div style={{ position: "sticky", top: "80px" }}>
          <div className="card" style={{ overflow: "visible" }}>
            <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: "0.65rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#94a3b8" }}>Sequence Flow</span>
              <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "#6366f1", background: "rgba(99,102,241,0.08)", padding: "0.15rem 0.5rem", borderRadius: "999px", border: "1px solid rgba(99,102,241,0.15)" }}>
                {steps.length} step{steps.length !== 1 ? "s" : ""}
              </span>
            </div>

            <div style={{ padding: "1.25rem 1rem" }}>
              {steps.map((step, idx) => {
                const configured = isStepConfigured(step);
                const isActive = activeStepIndex === idx;
                const hasAB = step.variants.length === 2;

                return (
                  <div key={idx}>
                    {/* ── Delay connector (between steps) ── */}
                    {idx > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", margin: "0 0 0 19px", gap: 0 }}>
                        <div style={{ width: "2px", height: "8px", background: "#e2e8f0" }} />
                        <div style={{
                          display: "flex", alignItems: "center", gap: "0.375rem",
                          padding: "0.3rem 0.7rem",
                          background: "#fafbff",
                          border: "1px solid rgba(99,102,241,0.15)",
                          borderRadius: "999px",
                          fontSize: "0.68rem", fontWeight: 700,
                          color: "#6366f1",
                          whiteSpace: "nowrap",
                          userSelect: "none",
                        }}>
                          <Clock size={10} />
                          Wait {step.delay} day{step.delay !== 1 ? "s" : ""}
                        </div>
                        <div style={{ width: "2px", height: "8px", background: "#e2e8f0" }} />
                      </div>
                    )}

                    {/* ── Step Node ── */}
                    <div
                      onClick={() => setActiveStepIndex(idx)}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "0.75rem",
                        cursor: "pointer",
                        position: "relative",
                      }}
                    >
                      {/* Node circle */}
                      <div style={{
                        width: "38px", height: "38px",
                        borderRadius: "50%",
                        background: isActive
                          ? "linear-gradient(135deg, #6366f1, #8b5cf6)"
                          : configured ? "#ffffff" : "#f8fafc",
                        border: isActive
                          ? "2px solid #6366f1"
                          : configured ? "2px solid #10b981" : "2px solid #e2e8f0",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                        boxShadow: isActive ? "0 0 0 4px rgba(99,102,241,0.12)" : configured ? "0 0 0 3px rgba(16,185,129,0.1)" : "none",
                        transition: "all 0.2s ease",
                        zIndex: 1,
                      }}>
                        {isActive ? (
                          <span style={{ fontSize: "0.8rem", fontWeight: 800, color: "white" }}>{idx + 1}</span>
                        ) : configured ? (
                          <CheckCircle2 size={16} style={{ color: "#10b981" }} />
                        ) : (
                          <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "#94a3b8" }}>{idx + 1}</span>
                        )}
                      </div>

                      {/* Step card */}
                      <div style={{
                        flex: 1,
                        padding: "0.625rem 0.75rem",
                        background: isActive ? "rgba(99,102,241,0.06)" : "#f8fafc",
                        border: `1px solid ${isActive ? "rgba(99,102,241,0.2)" : "#e2e8f0"}`,
                        borderRadius: "10px",
                        transition: "all 0.2s ease",
                        minWidth: 0,
                      }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.25rem" }}>
                          <span style={{ fontSize: "0.82rem", fontWeight: 700, color: isActive ? "#6366f1" : "#0f172a" }}>
                            Step {idx + 1}
                          </span>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                            {hasAB && (
                              <span style={{ fontSize: "0.58rem", fontWeight: 700, color: "#8b5cf6", background: "rgba(139,92,246,0.1)", border: "1px solid rgba(139,92,246,0.2)", borderRadius: "4px", padding: "0.1rem 0.35rem", letterSpacing: "0.04em" }}>A/B</span>
                            )}
                            {steps.length > 1 && (
                              <button
                                onClick={(e) => { e.stopPropagation(); deleteStep(idx); }}
                                style={{ width: "22px", height: "22px", display: "flex", alignItems: "center", justifyContent: "center", background: "transparent", border: "none", borderRadius: "5px", color: "#94a3b8", cursor: "pointer", transition: "all 0.15s" }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = "#fee2e2"; e.currentTarget.style.color = "#dc2626"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#94a3b8"; }}
                              >
                                <Trash2 size={11} />
                              </button>
                            )}
                          </div>
                        </div>
                        {/* Subject preview */}
                        <div style={{ fontSize: "0.72rem", color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {step.variants[0]?.subject
                            ? `✉ ${step.variants[0].subject}`
                            : <span style={{ color: "#94a3b8", fontStyle: "italic" }}>Not configured</span>
                          }
                        </div>
                      </div>
                    </div>

                    {/* Vertical line from step to next delay connector */}
                    {idx < steps.length - 1 && (
                      <div style={{ display: "flex", justifyContent: "flex-start", paddingLeft: "18px" }}>
                        <div style={{ width: "2px", height: "8px", background: "#e2e8f0" }} />
                      </div>
                    )}
                  </div>
                );
              })}

              {/* ── Add Step CTA ── */}
              <div style={{ marginTop: "1rem" }}>
                <button
                  onClick={addStep}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    padding: "0.6rem",
                    background: "transparent",
                    border: "1.5px dashed #cbd5e1",
                    borderRadius: "10px",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    color: "#64748b",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#6366f1"; e.currentTarget.style.color = "#6366f1"; e.currentTarget.style.background = "rgba(99,102,241,0.04)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#cbd5e1"; e.currentTarget.style.color = "#64748b"; e.currentTarget.style.background = "transparent"; }}
                >
                  <Plus size={15} />
                  Add Step
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ════ RIGHT — Editor Panel ════ */}
        <div>
          {activeStep ? (
            <div className="card">
              {/* ── Step Header ── */}
              <div style={{
                padding: "1.25rem 1.5rem",
                background: "linear-gradient(135deg, rgba(99,102,241,0.05) 0%, rgba(139,92,246,0.03) 100%)",
                borderBottom: "1px solid #f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.875rem" }}>
                  <div style={{
                    width: "42px", height: "42px",
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 4px 12px rgba(99,102,241,0.25)",
                    flexShrink: 0,
                  }}>
                    <Mail size={18} style={{ color: "white" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>
                      Step {activeStepIndex + 1}
                      <span style={{ fontWeight: 400, color: "#94a3b8", fontSize: "0.85rem", marginLeft: "0.5rem" }}>
                        of {steps.length}
                      </span>
                    </div>
                    <div style={{ fontSize: "0.78rem", color: "#64748b", marginTop: "1px", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      {isStepConfigured(activeStep) ? (
                        <><CheckCircle2 size={12} style={{ color: "#10b981" }} /> Ready to send</>
                      ) : (
                        <><AlertCircle size={12} style={{ color: "#f59e0b" }} /> Needs configuration</>
                      )}
                    </div>
                  </div>
                </div>

                {/* Navigation arrows + Preview */}
                <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
                  <button
                    disabled={activeStepIndex === 0}
                    onClick={() => setActiveStepIndex(activeStepIndex - 1)}
                    style={{ width: "30px", height: "30px", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", cursor: activeStepIndex === 0 ? "not-allowed" : "pointer", opacity: activeStepIndex === 0 ? 0.4 : 1, color: "#475569", transition: "all 0.15s" }}
                  >
                    <ChevronRight size={14} style={{ transform: "rotate(180deg)" }} />
                  </button>
                  <button
                    disabled={activeStepIndex === steps.length - 1}
                    onClick={() => setActiveStepIndex(activeStepIndex + 1)}
                    style={{ width: "30px", height: "30px", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", cursor: activeStepIndex === steps.length - 1 ? "not-allowed" : "pointer", opacity: activeStepIndex === steps.length - 1 ? 0.4 : 1, color: "#475569", transition: "all 0.15s" }}
                  >
                    <ChevronRight size={14} />
                  </button>
                  <button
                    onClick={openPreview}
                    style={{
                      display: "flex", alignItems: "center", gap: "0.4rem",
                      padding: "0 0.75rem",
                      height: "30px",
                      background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                      border: "none",
                      borderRadius: "8px",
                      color: "white",
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "opacity 0.15s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                  >
                    <Eye size={13} />
                    Preview
                  </button>
                </div>
              </div>

              {/* ── Variant Tabs ── */}
              <div style={{ padding: "1rem 1.5rem 0", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", gap: "0" }}>
                  {activeStep.variants.map((variant) => (
                    <button
                      key={variant.id}
                      onClick={() => setActiveVariantId(variant.id)}
                      style={{
                        display: "flex", alignItems: "center", gap: "0.5rem",
                        padding: "0.6rem 1.125rem",
                        background: "transparent",
                        border: "none",
                        borderBottom: activeVariantId === variant.id ? "2px solid #6366f1" : "2px solid transparent",
                        fontSize: "0.85rem",
                        fontWeight: activeVariantId === variant.id ? 700 : 500,
                        color: activeVariantId === variant.id ? "#6366f1" : "#64748b",
                        cursor: "pointer",
                        transition: "all 0.15s",
                        marginBottom: "-1px",
                      }}
                    >
                      <span style={{
                        width: "20px", height: "20px",
                        borderRadius: "6px",
                        background: activeVariantId === variant.id
                          ? (variant.id === "A" ? "#6366f1" : "#8b5cf6")
                          : "#e2e8f0",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "0.65rem", fontWeight: 800,
                        color: activeVariantId === variant.id ? "white" : "#94a3b8",
                        transition: "all 0.15s",
                      }}>
                        {variant.id}
                      </span>
                      Variant {variant.id}
                      {variant.id === "B" && (
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteVariant("B"); }}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: "0", display: "flex", marginLeft: "2px" }}
                          title="Remove Variant B"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </button>
                  ))}
                  {activeStep.variants.length < 2 && (
                    <button
                      onClick={addVariant}
                      style={{
                        display: "flex", alignItems: "center", gap: "0.375rem",
                        padding: "0.6rem 0.875rem",
                        background: "transparent",
                        border: "none",
                        borderBottom: "2px solid transparent",
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        color: "#94a3b8",
                        cursor: "pointer",
                        transition: "color 0.15s",
                        marginBottom: "-1px",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#8b5cf6"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#94a3b8"; }}
                    >
                      <FlaskConical size={13} />
                      + A/B Test
                    </button>
                  )}
                </div>

                {/* A/B Split indicator */}
                {hasAbTest && (
                  <div style={{ display: "flex", alignItems: "center", gap: "0.625rem", fontSize: "0.75rem", color: "#64748b" }}>
                    <span style={{ fontWeight: 600, color: "#6366f1" }}>A {splitA}%</span>
                    <div style={{ width: "80px", height: "6px", background: "#e2e8f0", borderRadius: "999px", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${splitA}%`, background: "linear-gradient(90deg, #6366f1, #8b5cf6)", borderRadius: "999px", transition: "width 0.3s" }} />
                    </div>
                    <span style={{ fontWeight: 600, color: "#8b5cf6" }}>B {splitB}%</span>
                  </div>
                )}
              </div>

              {/* ── Form Body ── */}
              {activeVariant && (
                <div style={{ padding: "1.5rem" }}>

                  {/* Subject line */}
                  <div style={{ marginBottom: "1.25rem" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.4rem" }}>
                      <label style={{ fontSize: "0.8rem", fontWeight: 700, color: "#374151", display: "flex", alignItems: "center", gap: "0.375rem" }}>
                        <Mail size={13} style={{ color: "#6366f1" }} />
                        Subject Line
                      </label>
                      <span style={{ fontSize: "0.68rem", color: activeVariant.subject.length > 60 ? "#dc2626" : "#94a3b8", fontWeight: 500 }}>
                        {activeVariant.subject.length}/80
                      </span>
                    </div>
                    <input
                      type="text"
                      placeholder="Write a compelling subject line…"
                      value={activeVariant.subject}
                      maxLength={80}
                      onChange={(e) => updateVariant(activeStepIndex, activeVariant.id, "subject", e.target.value)}
                      style={{
                        width: "100%",
                        padding: "0.7rem 1rem",
                        background: "#ffffff",
                        border: `1px solid ${activeVariant.subject ? "#d1d5db" : "#e2e8f0"}`,
                        borderRadius: "10px",
                        color: "#0f172a",
                        fontSize: "0.9rem",
                        fontWeight: 500,
                        outline: "none",
                        transition: "all 0.15s",
                        fontFamily: "inherit",
                        letterSpacing: "-0.01em",
                      }}
                      onFocus={(e) => { e.target.style.borderColor = "rgba(99,102,241,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.1)"; }}
                      onBlur={(e) => { e.target.style.borderColor = activeVariant.subject ? "#d1d5db" : "#e2e8f0"; e.target.style.boxShadow = "none"; }}
                    />
                  </div>

                  {/* Template picker */}
                  {templates.length > 0 && (
                    <div style={{ marginBottom: "1.25rem" }}>
                      <label style={{ fontSize: "0.8rem", fontWeight: 700, color: "#374151", display: "flex", alignItems: "center", gap: "0.375rem", marginBottom: "0.4rem" }}>
                        <FileText size={13} style={{ color: "#6366f1" }} />
                        Load from Template
                        <span style={{ fontSize: "0.68rem", fontWeight: 500, color: "#94a3b8", marginLeft: "0.25rem" }}>optional</span>
                      </label>
                      <div style={{ position: "relative" }}>
                        <select
                          value={activeVariant.templateId}
                          onChange={(e) => {
                            const tplId = e.target.value;
                            updateVariant(activeStepIndex, activeVariant.id, "templateId", tplId);
                            if (tplId) {
                              const tpl = templates.find((t) => String(t.id) === String(tplId));
                              if (tpl) {
                                updateVariant(activeStepIndex, activeVariant.id, "body", tpl.content || "");
                                updateVariant(activeStepIndex, activeVariant.id, "subject", tpl.subject || "");
                              }
                            }
                          }}
                          style={{
                            width: "100%",
                            padding: "0.65rem 2.25rem 0.65rem 1rem",
                            background: activeVariant.templateId ? "rgba(99,102,241,0.04)" : "#f8fafc",
                            border: `1px solid ${activeVariant.templateId ? "rgba(99,102,241,0.2)" : "#e2e8f0"}`,
                            borderRadius: "10px",
                            color: activeVariant.templateId ? "#6366f1" : "#64748b",
                            fontSize: "0.875rem",
                            fontWeight: activeVariant.templateId ? 600 : 400,
                            outline: "none",
                            cursor: "pointer",
                            fontFamily: "inherit",
                            appearance: "auto",
                            transition: "all 0.15s",
                          }}
                          onFocus={(e) => { e.target.style.borderColor = "rgba(99,102,241,0.4)"; e.target.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.08)"; }}
                          onBlur={(e) => { e.target.style.borderColor = activeVariant.templateId ? "rgba(99,102,241,0.2)" : "#e2e8f0"; e.target.style.boxShadow = "none"; }}
                        >
                          <option value="">— Select a template —</option>
                          {templates.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Email body */}
                  <div style={{ marginBottom: "1.5rem" }}>
                    <label style={{ fontSize: "0.8rem", fontWeight: 700, color: "#374151", display: "flex", alignItems: "center", gap: "0.375rem", marginBottom: "0.4rem" }}>
                      <Zap size={13} style={{ color: "#6366f1" }} />
                      Email Body
                    </label>
                    <div style={{ border: "1px solid #e2e8f0", borderRadius: "10px", overflow: "hidden", transition: "border-color 0.15s" }}
                      onFocusCapture={(e) => { e.currentTarget.style.borderColor = "rgba(99,102,241,0.35)"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.07)"; }}
                      onBlurCapture={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.boxShadow = "none"; }}
                    >
                      <WysiwygEditor
                        value={activeVariant.body}
                        onChange={(html) => updateVariant(activeStepIndex, activeVariant.id, "body", html)}
                        liquidPreview={previewLiquid}
                      />
                    </div>
                  </div>

                  {/* A/B Split percentage — only when B exists */}
                  {hasAbTest && (
                    <div style={{
                      padding: "1.25rem",
                      background: "linear-gradient(135deg, rgba(99,102,241,0.04) 0%, rgba(139,92,246,0.03) 100%)",
                      border: "1px solid rgba(99,102,241,0.12)",
                      borderRadius: "12px",
                      marginBottom: "1.5rem",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem" }}>
                        <FlaskConical size={15} style={{ color: "#8b5cf6" }} />
                        <span style={{ fontSize: "0.83rem", fontWeight: 700, color: "#0f172a" }}>A/B Split Distribution</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                            <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#6366f1" }}>Variant A</span>
                            <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#8b5cf6" }}>Variant B</span>
                          </div>
                          {/* Split bar */}
                          <div style={{ height: "8px", background: "#e2e8f0", borderRadius: "999px", overflow: "hidden", marginBottom: "0.5rem" }}>
                            <div style={{ height: "100%", width: `${activeStep.variants[0].split}%`, background: "linear-gradient(90deg, #6366f1, #8b5cf6)", borderRadius: "999px", transition: "width 0.3s" }} />
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span style={{ fontSize: "0.75rem", color: "#64748b" }}>{activeStep.variants[0].split}% of recipients</span>
                            <span style={{ fontSize: "0.75rem", color: "#64748b" }}>{100 - activeStep.variants[0].split}% of recipients</span>
                          </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", alignItems: "center" }}>
                          <label style={{ fontSize: "0.68rem", color: "#94a3b8", fontWeight: 600 }}>A%</label>
                          <input
                            type="number"
                            min="1"
                            max="99"
                            value={activeStep.variants[0].split}
                            onChange={(e) => {
                              const val = Math.min(99, Math.max(1, parseInt(e.target.value) || 1));
                              updateVariant(activeStepIndex, "A", "split", val);
                              updateVariant(activeStepIndex, "B", "split", 100 - val);
                            }}
                            style={{ width: "60px", padding: "0.4rem 0.5rem", border: "1px solid #e2e8f0", borderRadius: "8px", textAlign: "center", fontSize: "0.85rem", fontWeight: 700, color: "#6366f1", background: "#fff", fontFamily: "inherit", outline: "none" }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Delay / Send timing ── */}
                  <div style={{
                    padding: "1.25rem",
                    background: "#fafbfc",
                    border: "1px solid #f1f5f9",
                    borderRadius: "12px",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.875rem" }}>
                      <Clock size={15} style={{ color: "#64748b" }} />
                      <span style={{ fontSize: "0.83rem", fontWeight: 700, color: "#0f172a" }}>Send Timing</span>
                      {activeStepIndex === 0 && (
                        <span style={{ fontSize: "0.68rem", color: "#94a3b8", fontWeight: 500, marginLeft: "0.25rem" }}>First email — sent immediately</span>
                      )}
                    </div>
                    {activeStepIndex > 0 ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                        <span style={{ fontSize: "0.83rem", color: "#475569", fontWeight: 500 }}>Send</span>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <input
                            type="number"
                            min="1"
                            value={activeStep.delay}
                            onChange={(e) => {
                              const d = Math.max(1, parseInt(e.target.value) || 1);
                              setSteps((prev) => prev.map((s, idx) => idx === activeStepIndex ? { ...s, delay: d } : s));
                            }}
                            style={{ width: "72px", padding: "0.5rem 0.625rem", background: "#ffffff", border: "1px solid #d1d5db", borderRadius: "8px", color: "#0f172a", fontSize: "0.9rem", fontWeight: 700, textAlign: "center", fontFamily: "inherit", outline: "none", transition: "all 0.15s" }}
                            onFocus={(e) => { e.target.style.borderColor = "rgba(99,102,241,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.1)"; }}
                            onBlur={(e) => { e.target.style.borderColor = "#d1d5db"; e.target.style.boxShadow = "none"; }}
                          />
                          <span style={{ fontSize: "0.875rem", color: "#475569", fontWeight: 500 }}>
                            day{activeStep.delay !== 1 ? "s" : ""} after previous step
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#10b981", boxShadow: "0 0 0 3px rgba(16,185,129,0.15)" }} />
                        <span style={{ fontSize: "0.83rem", color: "#059669", fontWeight: 600 }}>Sends immediately when campaign starts</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* ── Empty State ── */
            <div className="card" style={{ padding: "4rem 2rem", textAlign: "center" }}>
              <div style={{ width: "72px", height: "72px", borderRadius: "50%", background: "linear-gradient(135deg, rgba(99,102,241,0.1), rgba(139,92,246,0.08))", border: "1px solid rgba(99,102,241,0.12)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.5rem" }}>
                <Mail size={28} style={{ color: "#6366f1" }} />
              </div>
              <h3 style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0f172a", marginBottom: "0.5rem" }}>No step selected</h3>
              <p style={{ fontSize: "0.875rem", color: "#64748b" }}>Click a step on the left to configure it</p>
            </div>
          )}
        </div>
      </div>

      {/* ════ Preview Modal ════ */}
      {showPreview && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "1.5rem",
          }}
          onClick={(e) => { if (e.target === e.currentTarget) { setShowPreview(false); setSelectedContact(null); } }}
        >
          <div style={{
            background: "#ffffff",
            borderRadius: "16px",
            boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
            width: "100%",
            maxWidth: "780px",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}>
            {/* Modal Header */}
            <div style={{
              padding: "1.125rem 1.5rem",
              borderBottom: "1px solid #f1f5f9",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              background: "linear-gradient(135deg, rgba(99,102,241,0.05), rgba(139,92,246,0.03))",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                <div style={{
                  width: "36px", height: "36px", borderRadius: "10px",
                  background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Eye size={16} style={{ color: "white" }} />
                </div>
                <div>
                  <div style={{ fontSize: "0.95rem", fontWeight: 800, color: "#0f172a" }}>
                    Email Preview — Step {activeStepIndex + 1}
                    {activeVariant && (
                      <span style={{ fontWeight: 500, color: "#6366f1", marginLeft: "0.4rem", fontSize: "0.82rem" }}>
                        Variant {activeVariant.id}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: "0.72rem", color: "#94a3b8", marginTop: "1px" }}>
                    Select a contact below to preview personalized content
                  </div>
                </div>
              </div>
              <button
                onClick={() => { setShowPreview(false); setSelectedContact(null); }}
                style={{ width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", background: "#f1f5f9", border: "none", borderRadius: "8px", cursor: "pointer", color: "#64748b" }}
              >
                <X size={16} />
              </button>
            </div>

            {previewLoading ? (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "4rem" }}>
                <RefreshCw size={24} style={{ color: "#6366f1", animation: "spin 1s linear infinite" }} />
              </div>
            ) : (
              <>
                {/* Controls bar */}
                <div style={{
                  padding: "0.875rem 1.5rem",
                  borderBottom: "1px solid #f1f5f9",
                  display: "flex", alignItems: "center", gap: "0.75rem",
                  flexWrap: "wrap",
                  background: "#fafbff",
                }}>
                  {/* Contact dropdown */}
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flex: "1 1 200px", minWidth: 0 }}>
                    <User size={14} style={{ color: "#6366f1", flexShrink: 0 }} />
                    <div style={{ position: "relative", flex: 1 }}>
                      <select
                        value={selectedContact?.id ?? ""}
                        onChange={(e) => {
                          const c = previewContacts.find((c) => String(c.id) === e.target.value);
                          setSelectedContact(c || null);
                        }}
                        style={{
                          width: "100%",
                          padding: "0.5rem 2rem 0.5rem 0.75rem",
                          background: "#fff",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          fontSize: "0.82rem",
                          color: selectedContact ? "#0f172a" : "#94a3b8",
                          fontWeight: selectedContact ? 500 : 400,
                          outline: "none",
                          cursor: "pointer",
                          appearance: "none",
                          fontFamily: "inherit",
                        }}
                      >
                        {previewContacts.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name || c.email} {c.name && c.email ? `(${c.email})` : ""}
                          </option>
                        ))}
                      </select>
                      <ChevronDown size={13} style={{ position: "absolute", right: "0.6rem", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }} />
                    </div>
                  </div>

                  <div style={{ width: "1px", height: "28px", background: "#e2e8f0", flexShrink: 0 }} />

                  {/* From account dropdown */}
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flex: "1 1 180px", minWidth: 0 }}>
                    <Mail size={14} style={{ color: "#6366f1", flexShrink: 0 }} />
                    <div style={{ position: "relative", flex: 1 }}>
                      <select
                        value={selectedFromAccount}
                        onChange={(e) => setSelectedFromAccount(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "0.5rem 2rem 0.5rem 0.75rem",
                          background: "#fff",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          fontSize: "0.82rem",
                          color: selectedFromAccount ? "#0f172a" : "#94a3b8",
                          fontWeight: selectedFromAccount ? 500 : 400,
                          outline: "none",
                          cursor: "pointer",
                          appearance: "none",
                          fontFamily: "inherit",
                        }}
                      >
                        <option value="">— Select sender —</option>
                        {emailAccounts.map((acc) => (
                          <option key={acc.id} value={acc.id}>{acc.email}</option>
                        ))}
                      </select>
                      <ChevronDown size={13} style={{ position: "absolute", right: "0.6rem", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }} />
                    </div>
                  </div>

                  {/* Send test email button */}
                  <button
                    onClick={handleSendTestEmail}
                    disabled={sendingTest || !selectedFromAccount}
                    style={{
                      display: "flex", alignItems: "center", gap: "0.4rem",
                      padding: "0.5rem 1rem",
                      background: sendingTest || !selectedFromAccount
                        ? "#e2e8f0"
                        : "linear-gradient(135deg, #6366f1, #8b5cf6)",
                      border: "none",
                      borderRadius: "8px",
                      color: sendingTest || !selectedFromAccount ? "#94a3b8" : "white",
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      cursor: sendingTest || !selectedFromAccount ? "not-allowed" : "pointer",
                      flexShrink: 0,
                      transition: "opacity 0.15s",
                    }}
                  >
                    {sendingTest
                      ? <RefreshCw size={13} style={{ animation: "spin 1s linear infinite" }} />
                      : <Send size={13} />}
                    {sendingTest ? "Sending…" : "Send Test Email"}
                  </button>
                </div>

                {/* Subject preview bar */}
                {activeVariant?.subject && (
                  <div style={{
                    padding: "0.625rem 1.5rem",
                    borderBottom: "1px solid #f1f5f9",
                    display: "flex", alignItems: "center", gap: "0.5rem",
                    background: "#f8fafc",
                    fontSize: "0.82rem",
                  }}>
                    <span style={{ color: "#94a3b8", fontWeight: 600, flexShrink: 0 }}>Subject:</span>
                    <span style={{ color: "#0f172a", fontWeight: 500 }}>
                      {renderedSubject || activeVariant.subject}
                    </span>
                  </div>
                )}

                {/* HTML Preview */}
                <div style={{ flex: 1, overflow: "hidden", background: "#f1f5f9", padding: "1rem" }}>
                  {activeVariant?.body ? (
                    <iframe
                      srcDoc={renderedBody || activeVariant.body}
                      style={{
                        width: "100%",
                        height: "100%",
                        minHeight: "400px",
                        border: "none",
                        borderRadius: "10px",
                        background: "white",
                      }}
                      sandbox="allow-same-origin"
                      title="Email preview"
                    />
                  ) : (
                    <div style={{
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      height: "300px", color: "#94a3b8", gap: "0.75rem",
                    }}>
                      <Mail size={40} style={{ opacity: 0.4 }} />
                      <span style={{ fontSize: "0.875rem" }}>No email body yet. Write content in the editor first.</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
